(() => {
  if (window.MayuraUsageReveal) {
    window.MayuraUsageReveal.init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observers = new Set();
  const rituals = new Set();
  let ritualFrame = null;

  const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);

  const revealAll = (container) => {
    container.classList.remove('is-scroll-ready');
    container.querySelectorAll('[data-mayura-reveal-item]').forEach((item) => item.classList.add('is-visible'));
  };

  const completeRitual = (container) => {
    container.classList.remove('is-scroll-ready');
    container.style.setProperty('--ritual-progress', '1');
    container.querySelectorAll('[data-ritual-step]').forEach((item) => item.classList.add('is-active'));
  };

  const updateRitual = (container) => {
    if (!container.isConnected) {
      rituals.delete(container);
      return;
    }

    const items = Array.from(container.querySelectorAll('[data-ritual-step]'));
    if (!items.length) return;

    const rect = container.getBoundingClientRect();
    const start = window.innerHeight * 0.84;
    const end = window.innerHeight * 0.3;
    const progress = clamp((start - rect.top) / Math.max(start - end, 1));
    container.style.setProperty('--ritual-progress', progress.toFixed(3));

    items.forEach((item, index) => {
      const activationPoint = items.length === 1 ? 0.15 : index / (items.length - 1);
      item.classList.toggle('is-active', progress > 0 && progress >= activationPoint);
    });
  };

  const updateRituals = () => {
    ritualFrame = null;
    rituals.forEach(updateRitual);
  };

  const scheduleRitualUpdate = () => {
    if (ritualFrame !== null) return;
    ritualFrame = window.requestAnimationFrame(updateRituals);
  };

  const initRevealContainer = (container) => {
    if (container.dataset.mayuraRevealInitialized === 'true') return;
    container.dataset.mayuraRevealInitialized = 'true';

    const items = Array.from(container.querySelectorAll('[data-mayura-reveal-item]'));
    if (!items.length || reducedMotion.matches || !('IntersectionObserver' in window)) {
      revealAll(container);
      return;
    }

    container.classList.add('is-scroll-ready');

    if (container.matches('[data-mayura-usage-reveal]')) {
      container.classList.add('is-sequenced');

      const sequenceObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry?.isIntersecting) return;

          items.forEach((item) => item.classList.add('is-visible'));
          sequenceObserver.disconnect();
        },
        { rootMargin: '0px 0px -14% 0px', threshold: 0.12 }
      );

      const containerRect = container.getBoundingClientRect();
      if (containerRect.bottom <= 0) {
        revealAll(container);
      } else {
        sequenceObserver.observe(container);
        observers.add(sequenceObserver);
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );

    items.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.88) {
        item.classList.add('is-visible');
      } else {
        observer.observe(item);
      }
    });
    observers.add(observer);
  };

  const initRitual = (container) => {
    if (container.dataset.mayuraRitualInitialized === 'true') return;
    container.dataset.mayuraRitualInitialized = 'true';

    if (reducedMotion.matches) {
      completeRitual(container);
      return;
    }

    container.classList.add('is-scroll-ready');
    rituals.add(container);
    scheduleRitualUpdate();
  };

  const init = (root = document) => {
    const sections = root.matches?.('.mayura-usage-section')
      ? [root]
      : Array.from(root.querySelectorAll?.('.mayura-usage-section') || []);

    sections.forEach((section) => {
      section.querySelectorAll('[data-mayura-usage-reveal], .mayura-usage-reveal__notes').forEach(initRevealContainer);
      section.querySelectorAll('[data-mayura-ritual]').forEach(initRitual);
    });
  };

  window.addEventListener('scroll', scheduleRitualUpdate, { passive: true });
  window.addEventListener('resize', scheduleRitualUpdate, { passive: true });

  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;

    observers.forEach((observer) => observer.disconnect());
    document.querySelectorAll('.mayura-usage-section').forEach((section) => {
      section.querySelectorAll('[data-mayura-usage-reveal], .mayura-usage-reveal__notes').forEach(revealAll);
      section.querySelectorAll('[data-mayura-ritual]').forEach(completeRitual);
    });
    rituals.clear();
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    event.target.closest('[data-mayura-reveal-item]')?.classList.add('is-visible');
    event.target.closest('[data-ritual-step]')?.classList.add('is-active');
  });

  window.MayuraUsageReveal = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
